import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AdminShell } from "../../components/AdminShell";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { api, erroMsg } from "../../lib/api";
import { brl, dataHora, pct } from "../../lib/format";
import { useMeta } from "../../context/MetaContext";
import { CheckCircle2, FileSpreadsheet, Loader2, Upload, X } from "lucide-react";

export default function AdminImportacao() {
  const { recarregar } = useMeta();
  const [arquivo, setArquivo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [analisando, setAnalisando] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [confirmar, setConfirmar] = useState(false);
  const [atualizarImpostos, setAtualizarImpostos] = useState(true);
  const [historico, setHistorico] = useState([]);
  const inputRef = useRef(null);

  const carregarHistorico = useCallback(() => {
    api.get("/admin/imports").then((r) => setHistorico(r.data.itens)).catch(() => {});
  }, []);

  useEffect(() => {
    carregarHistorico();
  }, [carregarHistorico]);

  const analisar = async (f) => {
    setArquivo(f);
    setPreview(null);
    setAnalisando(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await api.post("/admin/import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(r.data);
      toast.success("Planilha analisada com sucesso");
    } catch (e) {
      toast.error(erroMsg(e, "Não foi possível ler a planilha."));
      setArquivo(null);
    } finally {
      setAnalisando(false);
    }
  };

  const aplicar = async () => {
    if (!arquivo) return;
    setAplicando(true);
    const fd = new FormData();
    fd.append("file", arquivo);
    try {
      const r = await api.post("/admin/import/apply", fd, {
        params: { atualizar_impostos: atualizarImpostos },
        headers: { "Content-Type": "multipart/form-data" },
      });
      const res = r.data.resultado;
      toast.success(
        `Importação concluída: ${res.criados} criados, ${res.atualizados} atualizados`
      );
      setPreview(null);
      setArquivo(null);
      if (inputRef.current) inputRef.current.value = "";
      carregarHistorico();
      recarregar();
    } catch (e) {
      toast.error(erroMsg(e));
    } finally {
      setAplicando(false);
      setConfirmar(false);
    }
  };

  const r = preview?.resumo;

  return (
    <AdminShell
      titulo="Importar planilha"
      descricao="Envie a planilha de preços (.xlsx) para atualizar produtos e a matriz de impostos. Nada é gravado antes de você confirmar."
    >
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 bg-white p-5">
            <div
              className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 px-6 py-10 text-center"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const f = e.dataTransfer.files?.[0];
                if (f) analisar(f);
              }}
            >
              <span className="mb-3 grid h-12 w-12 place-items-center rounded-full bg-primary/10 text-primary">
                <FileSpreadsheet className="h-6 w-6" />
              </span>
              <div className="font-display text-[15px] font-semibold text-slate-900">
                {arquivo ? arquivo.name : "Selecione a planilha de preços"}
              </div>
              <p className="mt-1 max-w-md text-[12.5px] text-slate-500">
                Formato esperado: colunas A–L com dados do produto e, a partir da coluna M,
                grupos de 4 colunas por estado (Matéria-Prima, Revenda, Uso e Consumo, DIFAL).
              </p>
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.xlsm"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) analisar(f);
                }}
                data-testid="admin-importacao-upload-input"
              />
              <div className="mt-4 flex gap-2">
                <Button
                  onClick={() => inputRef.current?.click()}
                  disabled={analisando}
                  data-testid="admin-importacao-escolher"
                >
                  {analisando ? (
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="mr-1.5 h-4 w-4" />
                  )}
                  {analisando ? "Analisando…" : "Escolher arquivo"}
                </Button>
                {arquivo && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setArquivo(null);
                      setPreview(null);
                      if (inputRef.current) inputRef.current.value = "";
                    }}
                    data-testid="admin-importacao-limpar"
                  >
                    <X className="mr-1.5 h-4 w-4" /> Remover
                  </Button>
                )}
              </div>
            </div>
          </div>

          {preview && (
            <>
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="mb-3 font-display text-sm font-semibold text-slate-900">
                  Resumo da análise — aba “{preview.aba}”
                </div>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
                  {[
                    { l: "Na planilha", v: r.total_planilha },
                    { l: "Novos", v: r.novos, tom: "text-[#1B315E]" },
                    { l: "Alterados", v: r.alterados, tom: "text-amber-700" },
                    { l: "Sem mudança", v: r.sem_mudanca },
                    { l: "Sem preço", v: r.sem_preco },
                    { l: "NCMs", v: r.ncms },
                  ].map((k) => (
                    <div key={k.l} className="rounded-lg border border-slate-200 px-3 py-2">
                      <div className="text-[10.5px] font-semibold uppercase tracking-wide text-slate-500">
                        {k.l}
                      </div>
                      <div className={`num font-display text-[20px] font-bold ${k.tom || "text-slate-900"}`}>
                        {k.v}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-3 flex flex-wrap gap-1.5">
                  {preview.ncms.map((n) => (
                    <Badge key={n} variant="outline" className="font-mono-num text-[11px]">
                      {n}
                    </Badge>
                  ))}
                </div>

                {preview.conflitos?.length > 0 && (
                  <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
                    Atenção: {preview.conflitos.length} linha(s) apresentam alíquotas divergentes para
                    o mesmo NCM. Será usada a primeira ocorrência; ajuste depois em Impostos.
                  </div>
                )}
              </div>

              {preview.alterados?.length > 0 && (
                <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                  <div className="border-b border-slate-200 px-4 py-3 font-display text-sm font-semibold text-slate-900">
                    Produtos que serão alterados ({r.alterados})
                  </div>
                  <div className="max-h-[340px] overflow-auto">
                    <Table data-testid="admin-importacao-preview-table">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[130px]">Código</TableHead>
                          <TableHead>Produto</TableHead>
                          <TableHead>Mudanças</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {preview.alterados.map((a) => (
                          <TableRow key={a.codigo}>
                            <TableCell className="font-mono-num text-[12px]">{a.codigo}</TableCell>
                            <TableCell className="text-[12.5px]">{a.descricao_produto}</TableCell>
                            <TableCell className="text-[11.5px] text-slate-600">
                              {Object.entries(a.diffs).map(([campo, [de, para]]) => (
                                <div key={campo}>
                                  <b>{campo}</b>:{" "}
                                  <span className="text-slate-500">
                                    {campo === "valor_unitario" ? brl(de) : String(de)}
                                  </span>{" "}
                                  →{" "}
                                  <span className="font-medium text-slate-900">
                                    {campo === "valor_unitario" ? brl(para) : String(para)}
                                  </span>
                                </div>
                              ))}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {preview.novos?.length > 0 && (
                <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                  <div className="border-b border-slate-200 px-4 py-3 font-display text-sm font-semibold text-slate-900">
                    Produtos novos ({r.novos})
                  </div>
                  <div className="max-h-[300px] overflow-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[130px]">Código</TableHead>
                          <TableHead>Produto</TableHead>
                          <TableHead className="w-[100px]">NCM</TableHead>
                          <TableHead className="w-[110px] text-right">Valor</TableHead>
                          <TableHead className="w-[80px] text-right">IPI</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {preview.novos.map((n) => (
                          <TableRow key={n.codigo}>
                            <TableCell className="font-mono-num text-[12px]">{n.codigo}</TableCell>
                            <TableCell className="text-[12.5px]">{n.descricao_produto}</TableCell>
                            <TableCell className="font-mono-num text-[12px]">{n.ncm}</TableCell>
                            <TableCell className="num text-right text-[12.5px]">
                              {brl(n.valor_unitario)}
                            </TableCell>
                            <TableCell className="num text-right text-[12px]">{pct(n.ipi)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="h-fit space-y-3 lg:sticky lg:top-20">
          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="font-display text-sm font-semibold text-slate-900">Aplicar importação</div>
            <div className="mt-3 flex items-start justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2.5">
              <div>
                <Label className="text-[13px]">Atualizar matriz de impostos</Label>
                <p className="text-[11.5px] text-slate-500">
                  Se desligado, apenas produtos são atualizados e as alíquotas atuais são mantidas.
                </p>
              </div>
              <Switch
                checked={atualizarImpostos}
                onCheckedChange={setAtualizarImpostos}
                data-testid="admin-importacao-atualizar-impostos"
              />
            </div>
            <Button
              className="mt-3 h-11 w-full"
              disabled={!preview || aplicando}
              onClick={() => setConfirmar(true)}
              data-testid="admin-importacao-aplicar-button"
            >
              {aplicando ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle2 className="mr-2 h-4 w-4" />
              )}
              Aplicar importação
            </Button>
            <p className="mt-2 text-[11.5px] leading-relaxed text-slate-500">
              Produtos existentes são identificados pelo código. O status ativo/inativo definido
              manualmente é preservado.
            </p>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="font-display text-sm font-semibold text-slate-900">
              Histórico de importações
            </div>
            <ul className="mt-2 space-y-2">
              {historico.length === 0 && (
                <li className="text-[12px] text-slate-500">Nenhuma importação registrada.</li>
              )}
              {historico.map((h) => (
                <li key={h.id} className="rounded-lg bg-slate-50 px-3 py-2">
                  <div className="truncate text-[12.5px] font-medium text-slate-900">
                    {h.arquivo}
                  </div>
                  <div className="text-[11px] text-slate-500">
                    {dataHora(h.criado_em)} • {h.usuario}
                  </div>
                  <div className="mt-0.5 text-[11px] text-slate-600">
                    {h.criados} criados • {h.atualizados} atualizados • {h.ncms} NCMs
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <AlertDialog open={confirmar} onOpenChange={setConfirmar}>
        <AlertDialogContent className="bg-white" data-testid="admin-importacao-confirmar-alertdialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar importação?</AlertDialogTitle>
            <AlertDialogDescription>
              Serão criados {r?.novos} produtos e atualizados {r?.alterados}.{" "}
              {atualizarImpostos
                ? `A matriz de impostos de ${r?.ncms} NCM(s) será substituída pelos valores da planilha.`
                : "A matriz de impostos atual será mantida."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={aplicar} data-testid="admin-importacao-confirmar-button">
              Confirmar e aplicar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminShell>
  );
}
